import Decimal from 'decimal.js';
import type { ProductCatalog as V3ProductCatalog } from '@hotjar/product-catalog';

import { DatadogTeam, createDatadogLogger } from '@hotjar/webapp/lib/createDatadogLogger';

import { legacyPlanIdToV3Sku } from '~/product-catalog/constants/map-dictionary';
import type { PricingTable } from './pricing/types';

export const upsellLogger = createDatadogLogger('upsellLogger', DatadogTeam.BuyerXP);

type PricingItem = {
  id: string;
  price: number;
};

const productCatalogToPricingTable = (
  v3ProductCatalog: V3ProductCatalog | undefined,
): PricingTable | null => {
  if (!v3ProductCatalog) {
    return null;
  }

  const ask = v3ProductCatalog.find((product) => product.id === 'PROD-1');
  const observe = v3ProductCatalog.find((product) => product.id === 'PROD-2');
  const v3SkuToLegacyPlanId = Object.fromEntries(
    Object.entries(legacyPlanIdToV3Sku).map((a) => a.reverse()),
  );

  if (!ask || !observe) {
    throw new Error('productCatalogToPricingTable: should return an ask or observe product');
  }

  /**
   * Create and array of PricingItems for observe products
   * example:
   * [
   *    {
   *        id: analytics_basic_2000
   *        price: 0
   *    },
   *    ...
   * ]
   */
  const observePricing: PricingItem[] = [];
  observe.plans.forEach((plan) => {
    plan.allowances.forEach((allowance) => {
      observePricing.push({ id: v3SkuToLegacyPlanId[allowance.sku], price: allowance.price });
    });
  });

  /**
   * Create and array of PricingItems for ask products
   * example:
   * [
   *    {
   *        id: feedback_basic_20
   *        price: 0
   *    },
   *    ...
   * ]
   */
  const askPricing: PricingItem[] = [];
  ask.plans.forEach((plan) => {
    plan.allowances.forEach((allowance) => {
      askPricing.push({ id: v3SkuToLegacyPlanId[allowance.sku], price: allowance.price });
    });
  });

  /**
   * Create multi-sku pricing table from the ask and observe pricing item
   * by concatenating their plan_ids and summing thier prices
   */
  let pricingTable = {};
  observePricing.forEach((observeItem) => {
    askPricing.forEach((askItem) => {
      pricingTable = {
        ...pricingTable,
        [`${observeItem.id};${askItem.id}`]: new Decimal(observeItem.price + askItem.price),
      };
    });
  });

  return pricingTable;
};

export { productCatalogToPricingTable };
